import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { format } from 'date-fns';
import { DevFinancial } from '../../../../types/financial';
import { useQuery } from '@tanstack/react-query';
import { supabaseCommunityService } from '../../../../services/supabase/communities';
import type { Community } from '../../../../types/community';
import type { Project } from '../../../../types/project';
import type { Supplier } from '../../../../types/supplier';

interface EditFinancialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  financial: DevFinancial;
}

type RecordType = 'income' | 'expense' | 'both';

export default function EditFinancialModal({
  isOpen,
  onClose,
  onSuccess,
  financial
}: EditFinancialModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Determine record type based on which field has a value
  const recordType: RecordType = useMemo(() => {
    const hasIncome = financial.fin_inc_amt !== null && financial.fin_inc_amt !== undefined && financial.fin_inc_amt > 0;
    const hasExpense = financial.fin_exp_amt !== null && financial.fin_exp_amt !== undefined && financial.fin_exp_amt > 0;
    
    if (hasIncome && hasExpense) return 'both';
    if (hasIncome) return 'income';
    return 'expense';
  }, [financial]);

  // Set title and colors based on record type
  const modalTitle = useMemo(() => {
    switch (recordType) {
      case 'income': return 'Edit Income Record';
      case 'expense': return 'Edit Expense Record';
      case 'both': return 'Edit Financial Record';
      default: return 'Edit Financial Record';
    }
  }, [recordType]);

  // Theme colors based on record type
  const themeColor = useMemo(() => {
    switch (recordType) {
      case 'income': return 'emerald';
      case 'expense': return 'red';
      case 'both': return 'blue';
      default: return 'blue';
    }
  }, [recordType]);

  // Fetch communities
  const { 
    data: communities, 
    isLoading: isLoadingCommunities, 
    error: communitiesError 
  } = useQuery<Community[]>({
    queryKey: ['communities'],
    queryFn: supabaseCommunityService.getAll,
    enabled: isOpen
  });

  // Fetch projects
  const {
    data: projects,
    isLoading: isLoadingProjects,
    error: projectsError
  } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('proj_id');

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen
  });

  // Fetch suppliers
  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    error: suppliersError
  } = useQuery<Supplier[]>({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('suppl_abrev');

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen
  });

  // Form state initialization
  const [formData, setFormData] = useState({
    community: financial.fin_comm || '',
    date: financial.fin_date || format(new Date(), 'yyyy-MM-dd'),
    incomeAmount: financial.fin_inc_amt?.toString() || '',
    expenseAmount: financial.fin_exp_amt?.toString() || '',
    description: financial.fin_desc || '',
    expenseProject: financial.fin_exp_proj || '',
    acquisitionDate: financial.fin_acq_date || '',
    supplier: financial.fin_supplier || ''
  });

  // Debug logging
  useEffect(() => {
    if (communitiesError) {
      console.error('Error fetching communities:', communitiesError);
    }
    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
    }
    if (suppliersError) {
      console.error('Error fetching suppliers:', suppliersError);
    }
  }, [communitiesError, projectsError, suppliersError]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Process form data based on record type
      let updatedData: Record<string, any> = {
        fin_comm: formData.community,
        fin_date: formData.date || null,
        fin_desc: formData.description || null,
        fin_supplier: formData.supplier || null, // Supplier is now always included
      };

      // Add specific fields based on record type
      if (recordType === 'income' || recordType === 'both') {
        updatedData.fin_inc_amt = formData.incomeAmount ? parseFloat(formData.incomeAmount) : null;
      }

      if (recordType === 'expense' || recordType === 'both') {
        updatedData.fin_exp_amt = formData.expenseAmount ? parseFloat(formData.expenseAmount) : null;
        updatedData.fin_exp_proj = formData.expenseProject || null;
        updatedData.fin_acq_date = formData.acquisitionDate || null;
        // fin_supplier assignment removed from here as it's handled above
      }

      console.log('Updating financial record with data:', updatedData);

      // Update the financial record
      const { error: updateError } = await supabase
        .from('dev_financials')
        .update(updatedData)
        .match({
          fin_id: financial.fin_id
        });

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating financial record:', err);
      setError(err instanceof Error ? err.message : 'Failed to update financial record');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
        <div className={`flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10 text-${themeColor}-700`}>
          <h3 className="text-lg font-medium">{modalTitle}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Common fields */}
          {/* Community (always visible) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Community *
            </label>
            {isLoadingCommunities && (
              <div className="mt-1 text-sm text-gray-500">Loading communities...</div>
            )}
            {communitiesError && (
              <div className="mt-1 text-sm text-red-500">
                Error loading communities
              </div>
            )}
            <div className="relative">
              <select
                name="community"
                value={formData.community}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-${themeColor}-500 focus:ring-${themeColor}-500 appearance-none bg-white cursor-pointer hover:border-${themeColor}-500`}
                required
                disabled={isLoadingCommunities}
              >
                <option value="">Select a Community</option>
                {communities?.map((community) => (
                  <option 
                    key={community.comm_id} 
                    value={community.comm_id}
                    className="py-1"
                    title={community.comm_name}
                  >
                    {community.comm_id}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-500">Community name</p>
          </div>

          {/* Date (always visible) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-${themeColor}-500 focus:ring-${themeColor}-500`}
              required
            />
            <p className="mt-1 text-xs text-gray-500">Transaction date</p>
          </div>

          {/* Income Amount (visible for income or both) */}
          {(recordType === 'income' || recordType === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Income Amount *
              </label>
              <input
                type="number"
                step="0.01"
                name="incomeAmount"
                value={formData.incomeAmount}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-emerald-500 focus:ring-emerald-500`}
                placeholder="Enter income amount"
                required={recordType === 'income'}
              />
              <p className="mt-1 text-xs text-gray-500">Income amount in dollars</p>
            </div>
          )}

          {/* Expense Amount (visible for expense or both) */}
          {(recordType === 'expense' || recordType === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expense Amount *
              </label>
              <input
                type="number"
                step="0.01"
                name="expenseAmount"
                value={formData.expenseAmount}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-red-500`}
                placeholder="Enter expense amount"
                required={recordType === 'expense'}
              />
              <p className="mt-1 text-xs text-gray-500">Expense amount in dollars</p>
            </div>
          )}

          {/* Description (always visible) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-${themeColor}-500 focus:ring-${themeColor}-500`}
              placeholder="Enter description"
              maxLength={200}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.description.length}/200 characters - Description of the financial transaction
            </p>
          </div>

          {/* Supplier (now always visible) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Supplier
            </label>
            {isLoadingSuppliers && (
              <div className="mt-1 text-sm text-gray-500">Loading suppliers...</div>
            )}
            {suppliersError && (
              <div className="mt-1 text-sm text-red-500">
                Error loading suppliers
              </div>
            )}
            <div className="relative">
              <select
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-${themeColor}-500 focus:ring-${themeColor}-500 appearance-none bg-white cursor-pointer hover:border-${themeColor}-500`}
                disabled={isLoadingSuppliers}
              >
                <option value="">Select a Supplier</option>
                {suppliers?.map((supplier) => (
                  <option 
                    key={supplier.suppl_abrev} 
                    value={supplier.suppl_abrev}
                    className="py-1"
                  >
                    {supplier.supplier_name} ({supplier.suppl_abrev})
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-500">Supplier or vendor name</p>
          </div>

          {/* Expense-specific fields (visible for expense or both) */}
          {(recordType === 'expense' || recordType === 'both') && (
            <>
              {/* Expense Project */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Expense Project
                </label>
                {isLoadingProjects && (
                  <div className="mt-1 text-sm text-gray-500">Loading projects...</div>
                )}
                {projectsError && (
                  <div className="mt-1 text-sm text-red-500">
                    Error loading projects
                  </div>
                )}
                <div className="relative">
                  <select
                    name="expenseProject"
                    value={formData.expenseProject}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-red-500 appearance-none bg-white cursor-pointer hover:border-red-500`}
                    disabled={isLoadingProjects}
                  >
                    <option value="">Select a Project</option>
                    {projects?.map((project) => (
                      <option 
                        key={project.proj_id} 
                        value={project.proj_id}
                        className="py-1"
                      >
                        {project.proj_desc} ({project.proj_id})
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500">Associated project for this expense</p>
              </div>

              {/* Acquittal Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Acquittal Date
                </label>
                <input
                  type="date"
                  name="acquisitionDate"
                  value={formData.acquisitionDate}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-red-500`}
                />
                <p className="mt-1 text-xs text-gray-500">Date of financial acquittal if applicable</p>
              </div>
            </>
          )}

          {/* Form buttons */}
          <div className="mt-6 flex justify-end gap-3 sticky bottom-0 pt-4 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 bg-${themeColor}-600 text-white rounded-md text-sm font-medium hover:bg-${themeColor}-700 disabled:opacity-50`}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
